import React from "react";
import "./App.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { useGA4 } from "./hooks/useGA4";
import { useResetServerRenderedHead } from "./hooks/useResetServerRenderedHead";
import { SuperTeacherNavbar } from "./common/Navbar";
import {
  AppFlowPage,
  AppFlowInnerMode,
  UPGRADE_STAGES,
  DEFAULT_UPGRADED_USER_DESTINATION,
} from "./pages/AppFlowPage";
import { UpsellBanner } from "./pages/UpsellBanner";
import { UnitPage } from "./pages/UnitPage";
import { LessonPage } from "./pages/LessonPage";
import { PracticePage } from "./pages/PracticePage";
import { MyClassPage } from "./pages/MyClassPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AccountProvider } from "./common/context/AccountProvider";
import { AnalyticsProvider } from "./common/context/AnalyticsProvider";
import { ClientConfigProvider } from "./common/context/ClientConfigProvider";
import { JwtProvider } from "./common/context/JwtProvider";
import { ApiProvider } from "./common/context/ApiProvider";
import { RetryProvider } from "./common/context/RetryProvider";
import { VerifyAgeProvider } from "./common/context/VerifyAgeProvider";
import { PreferencesProvider } from "./common/context/PreferencesProvider";
import { LoginPage } from "./pages/LoginPage";
import { OneTimeTokenLoginPage } from "./pages/OneTimeTokenLoginPage";
import { EditProfilePage } from "./pages/EditProfilePage";
import { StudentAssignmentsPage } from "./pages/StudentAssignmentsPage";
import { TabsProvider } from "./common/context/TabsProvider";
import { SelectedFiltersProvider } from "./common/context/SelectedFiltersProvider";
import { NavProvider } from "./common/context/NavProvider";
import { NavContext } from "./common/context/NavContext";
import { LoadingIndicatorProvider } from "./common/context/LoadingIndicatorProvider";
import { LessonAssignmentsPage } from "./pages/LessonAssignmentsPage";
import { CustomSectionPage } from "./pages/CustomSectionPage";
import { SchoolDashboardPage } from "./pages/SchoolDashboardPage";
import { FetchedHtmlPage } from "./pages/FetchedHtmlPage";
import { SchoolGroupListPage } from "./pages/SchoolGroupListPage";
import { SchoolInsightReportListPage } from "./pages/SchoolInsightReportListPage";
import { SchoolListPage } from "./pages/SchoolListPage";
import { SchoolRenewalsPage } from "./pages/SchoolRenewalsPage";
import { CreateCustomFlashpackPage } from "./pages/CreateCustomFlashpackPage";
import { HomePageRedirector, HomePage } from "./pages/HomePage";
import { LessonRecordingsPage } from "./pages/LessonRecordingsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LegalPage } from "./pages/LegalPage";
import StandardsAttributionsPage from "./pages/StandardsAttributionsPage";
import { ManageAccountPage } from "./pages/ManageAccountPage";
import {
  NonSchoolUserRoute,
  NonSchoolStudentUserRoute,
} from "./pages/SchoolUserRoute";
import { DataHandlingPage } from "./pages/DataHandlingPage";
import { FeedbackPage } from "./pages/FeedbackPage";
import { TeacherResourcesPage } from "./pages/TeacherResourcesPage";

const muiTheme = createTheme({
  palette: {
    primary: {
      main: "#0A0907",
    },
    action: {
      hover: "rgba(0,0,0,0.04)",
      selected: "rgba(0,0,0,0.08)",
    },
  },
  typography: {
    fontFamily: "Inter, sans-serif",
  },
});

const InsideRouter = () => {
  const { resetNavigationBoundary } = React.useContext(NavContext);

  // Before useGA4, so a pageview after navigating records the title the visitor now sees.
  useResetServerRenderedHead();
  useGA4("G-G2RWRNY18S");

  React.useEffect(() => {
    resetNavigationBoundary();
  }, [resetNavigationBoundary]);
  return (
    <LoadingIndicatorProvider>
      <RetryProvider>
        <VerifyAgeProvider>
          <ClientConfigProvider>
            <JwtProvider>
              <PreferencesProvider>
                <AnalyticsProvider>
                  <AccountProvider>
                    <ApiProvider>
                      <Switch>
                        <Route path="/account/upgrade/">
                          <NonSchoolUserRoute>
                            <AppFlowPage
                              appFlowMode={{
                                mode: AppFlowInnerMode.Upgrade,
                                config: {
                                  stages: UPGRADE_STAGES,
                                  destinations: [
                                    DEFAULT_UPGRADED_USER_DESTINATION,
                                  ],
                                },
                              }}
                            />
                          </NonSchoolUserRoute>
                        </Route>
                        <Route path="/embed/schools/dashboard/">
                          <SchoolDashboardPage />
                        </Route>
                        <SelectedFiltersProvider>
                          <SuperTeacherNavbar>
                            <main>
                              <UpsellBanner />
                              <Switch>
                                <Route path="/home/">
                                  <HomePage />
                                </Route>
                                <Route path="/lessons/:lessonId/:lessonSlug/flashpacks/:customFlashpackId/">
                                  <LessonPage />
                                </Route>
                                <Route path="/lessons/:lessonId/flashpacks/:customFlashpackId/">
                                  <LessonPage />
                                </Route>
                                <Route path="/lessons/create-flashpack/">
                                  <CreateCustomFlashpackPage />
                                </Route>
                                <Route path="/lessons/:lessonId/:lessonSlug/">
                                  <LessonPage />
                                </Route>
                                <Route path="/lessons/:lessonId/">
                                  <LessonPage />
                                </Route>
                                <Route path="/courses/:unitId/:unitSlug/">
                                  <UnitPage />
                                </Route>
                                <Route path="/courses/:unitId/">
                                  <UnitPage />
                                </Route>
                                <Route path="/schools/all/">
                                  <SchoolListPage />
                                </Route>
                                <Route path="/schools/groups/">
                                  <SchoolGroupListPage />
                                </Route>
                                <Route path="/schools/renewals/">
                                  <SchoolRenewalsPage />
                                </Route>
                                <Route path="/schools/insights/">
                                  <SchoolInsightReportListPage />
                                </Route>
                                <Route path="/schools/dashboard/">
                                  <SchoolDashboardPage />
                                </Route>
                                <Route path="/practice/">
                                  <PracticePage />
                                </Route>
                                <Route path="/school/:schoolId/custom-section/:sectionId/">
                                  <CustomSectionPage />
                                </Route>
                                <Route path="/school/:schoolId/custom-section/">
                                  <CustomSectionPage />
                                </Route>
                                <Route path="/my-class/assignments/">
                                  <LessonAssignmentsPage />
                                </Route>
                                <Route path="/student-assignments/:teacherId/">
                                  <StudentAssignmentsPage />
                                </Route>
                                <Route path="/my-class/">
                                  <MyClassPage />
                                </Route>
                                <Route path="/profile/">
                                  <ProfilePage />
                                </Route>
                                <Redirect from="/awards/" to="/profile/" />
                                <Route path="/account/login/token/:token/">
                                  <OneTimeTokenLoginPage />
                                </Route>
                                <Route path="/account/login/token/">
                                  <OneTimeTokenLoginPage />
                                </Route>
                                <Route path="/account/login/">
                                  <LoginPage />
                                </Route>
                                <Route path="/account/login/clever/">
                                  <LoginPage />
                                </Route>
                                <Route path="/account/login/classlink/">
                                  <LoginPage />
                                </Route>
                                <Route path="/account/profile/edit/:profileId/">
                                  <EditProfilePage isForCreate={false} />
                                </Route>
                                <Route path="/account/profile/add/">
                                  <EditProfilePage isForCreate={true} />
                                </Route>
                                <Route path="/settings/">
                                  <SettingsPage />
                                </Route>
                                <Redirect from="/for-parents/" to="/settings/" />
                                <Route path="/legal/">
                                  <LegalPage />
                                </Route>
                                <Route path="/account/manage/">
                                  <NonSchoolUserRoute>
                                    <ManageAccountPage />
                                  </NonSchoolUserRoute>
                                </Route>
                                <Route path="/data-handling/">
                                  <DataHandlingPage />
                                </Route>
                                <Route path="/account/lesson-replays/">
                                  <LessonRecordingsPage />
                                </Route>
                                <Route path="/privacy-policy/">
                                  <FetchedHtmlPage apiQueryPath="legal/privacy-policy-content/" />
                                </Route>
                                <Route path="/terms-of-use/">
                                  <FetchedHtmlPage apiQueryPath="legal/terms-of-use-content/" />
                                </Route>
                                <Route path="/standards-attributions/">
                                  <StandardsAttributionsPage />
                                </Route>
                                <Route path="/feedback/">
                                  <NonSchoolStudentUserRoute>
                                    <FeedbackPage />
                                  </NonSchoolStudentUserRoute>
                                </Route>
                                <Route path="/teacher-resources/">
                                  <TeacherResourcesPage />
                                </Route>
                                <Route
                                  path="/"
                                  component={HomePageRedirector}
                                ></Route>
                              </Switch>
                            </main>
                          </SuperTeacherNavbar>
                        </SelectedFiltersProvider>
                      </Switch>
                    </ApiProvider>
                  </AccountProvider>
                </AnalyticsProvider>
              </PreferencesProvider>
            </JwtProvider>
          </ClientConfigProvider>
        </VerifyAgeProvider>
      </RetryProvider>
    </LoadingIndicatorProvider>
  );
};

export default function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <Router>
        <TabsProvider>
          <NavProvider>
            <InsideRouter />
          </NavProvider>
        </TabsProvider>
      </Router>
    </ThemeProvider>
  );
}
